import { useCallback, useRef, useState } from 'react';
import { GstMasterListItem } from '../../../../../store/company/types';

export interface OptimisticConsent {
    /** ISO timestamp captured client-side the moment the OTP submit succeeds. Used only for
     * display (GstFetch's `consentLineFor`) — never for reconciliation (see below). */
    at: string;
}

/**
 * Holds a client-side "Last consent" value per GSTIN until an async SNS-written row lands and
 * a refetch confirms it (compares against the last *observed* server value, not a client
 * clock, so it isn't fooled by clock skew).
 *
 * `lastSeenServerValue` is read and written once per call, in the callback body, before
 * `setPending` runs — not inside the updater, since React StrictMode double-invokes updaters
 * with the same previous state and a ref write there would leak across passes.
 */
export default function useOptimisticConsent() {
    const [pending, setPending] = useState<Record<string, OptimisticConsent>>({});
    const lastSeenServerValue = useRef<Record<string, number | undefined>>({});

    const setOptimisticConsent = useCallback((gstin: string, value: OptimisticConsent) => {
        setPending((prev) => ({ ...prev, [gstin]: value }));
    }, []);

    /** Call with the latest `gstMasterList` after every refetch. Reconciles in place — never
     * re-sorts or otherwise mutates the list itself. */
    const reconcileOptimisticConsent = useCallback((gstMasterList: GstMasterListItem[] | undefined) => {
        if (!gstMasterList?.length) return;

        const changedGstins = new Set<string>();
        for (const gst of gstMasterList) {
            const previousServerValue = lastSeenServerValue.current[gst.gstin];
            // No lastConsentAt yet on the server -> the SNS row hasn't landed. Keep holding,
            // and don't let an absent value overwrite the last-known baseline.
            if (gst.lastConsentAt && gst.lastConsentAt !== previousServerValue) {
                changedGstins.add(gst.gstin);
            }
            if (gst.lastConsentAt !== undefined) {
                lastSeenServerValue.current[gst.gstin] = gst.lastConsentAt;
            }
        }
        if (!changedGstins.size) return;

        setPending((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const gstin of changedGstins) {
                if (prev[gstin]) {
                    delete next[gstin];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, []);

    return { pendingConsents: pending, setOptimisticConsent, reconcileOptimisticConsent };
}
